import { createBrowserRouter, Navigate, useLocation, useParams } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import App from '@/App';
import { RouteGuard } from '@features/auth/shared/components';
import { AppShell } from '@shared/layout';
import { LoadingSkeleton, RouteErrorBoundary } from '@shared/components';
import { useAuth } from '@shared/hooks/useAuth';
import {
  getLocaleFromPathname,
  localizePath,
  resolvePreferredLocale,
  useLocale,
} from '@shared/i18n';
import { localizeRoutes } from './routeLocalization';

// Main dashboard component that handles role-based routing
const DashboardRouter = lazy(() =>
  import('@shared/components/dashboard/DashboardRouter').then(m => ({ default: m.DashboardRouter }))
);
const MESSAGING_ENABLED = import.meta.env.VITE_FEATURE_MESSAGING !== 'false';
const MessagesPage = lazy(() => import('@features/messages'));

// Auth pages
const SignIn = lazy(() => import('@features/auth/pages/sign-in'));
const PatientSignIn = lazy(() => import('@features/auth/pages/patient-sign-in'));
const StaffSignIn = lazy(() => import('@features/auth/pages/jema-partner-sign-in'));
const ForgotPassword = lazy(() => import('@features/auth/pages/forgot-password'));
const ResetPassword = lazy(() => import('@features/auth/pages/reset-password'));
const MagicLinkCallback = lazy(() => import('@features/auth/pages/magic-link-callback'));
const MagicLinkRequired = lazy(() => import('@features/auth/pages/magic-link-required'));
const AuthError = lazy(() => import('@features/auth/pages/auth-error'));
const SetupPassword = lazy(() => import('@features/auth/pages/setup-password'));

// Patient docs portal (token + OTP, no login)
const DocsPortalOtpPage = lazy(() => import('@features/shared/pages/docs-portal/OtpPage'));
const DocsPortalUploadPage = lazy(() => import('@features/shared/pages/docs-portal/UploadPage'));
const FlightsPortalOtpPage = lazy(() => import('@features/shared/pages/flights-portal/OtpPage'));
const FlightsPortalFormPage = lazy(() => import('@features/shared/pages/flights-portal/FormPage'));
const UserSettingsPage = lazy(() => import('@features/shared/pages/settings'));

// Patient pages
const PatientQuotesPage = lazy(() => import('@features/patient/pages/quotes'));
const PatientTravelPage = lazy(() => import('@features/patient/pages/travel/Page'));
const PatientPaymentsPage = lazy(() => import('@features/patient/pages/payments'));
const PatientTreatmentPage = lazy(() => import('@features/patient/pages/treatment'));

// Doctor pages
const DoctorMyDayPage = lazy(() => import('@features/doctor/pages/my-day'));
const DoctorPatientsPage = lazy(() => import('@features/doctor/pages/patients'));
const DoctorQuotesPage = lazy(() => import('@features/doctors/quotes'));
const DoctorCalendarPage = lazy(() => import('@features/doctor/pages/calendar'));
const DoctorTemplatesPage = lazy(() => import('@features/doctor/pages/templates'));
const DoctorPaymentsPage = lazy(() => import('@features/doctor/pages/payments'));
const DoctorInvitationAcceptPage = lazy(() => import('@features/doctor/pages/invitation-accept'));
const DoctorOrganizationManagePage = lazy(
  () => import('@features/doctor/pages/organization-manage')
);
const DoctorProfilePage = lazy(() => import('@features/doctor/pages/profile'));
const DoctorQuickOnboardingPage = lazy(() => import('@features/doctor/pages/quick-onboarding'));
const DoctorDiditCallbackPage = lazy(() => import('@features/doctor/pages/didit-callback/Page'));
const DoctorOnboarding = lazy(() => import('@features/doctor/onboarding'));
const DoctorAccountControlApprovalPage = lazy(
  () => import('@features/shared/pages/doctor-account-control/ApprovalPage')
);
const OwnershipTransferAcceptPage = lazy(
  () => import('@features/shared/pages/ownership-transfer-accept')
);
const DoctorPublicProfilePage = lazy(() => import('@features/doctor/pages/public-profile'));

// Admin pages
const AdminPatientsPage = lazy(() => import('@features/admin/pages/patients'));
const AdminQuotingQueuePage = lazy(() => import('@features/admin/pages/quoting-queue'));
const AdminCalendarPage = lazy(() => import('@features/admin/pages/calendar'));
const AdminLogisticsPage = lazy(() => import('@features/admin/pages/logistics'));
const AdminSalesPage = lazy(() => import('@features/admin/pages/sales'));
const AdminPaymentsPage = lazy(() => import('@features/admin/pages/payments'));
const AdminContractsPage = lazy(() => import('@features/admin/pages/contracts'));
const AdminReportsPage = lazy(() => import('@features/admin/pages/reports'));
const AdminUserManagementPage = lazy(() => import('@features/admin/pages/user-management'));
const AdminDoctorAccountControlWorkspacePage = lazy(
  () => import('@features/admin/pages/doctor-account-control/Page')
);
const AdminTravelPlanningPage = lazy(
  () => import('@features/quoting-queue/pages/travel-planning/TravelPlanningPage')
);
const AdminSpecialtyProcedureCatalogPage = lazy(
  () => import('@features/admin/pages/specialty-procedure-catalog')
);

// Super Admin pages
const SuperAdminUserManagement = lazy(() => import('@features/super-admin/pages/user-management'));
const SuperAdminOrganizationManagement = lazy(
  () => import('@features/super-admin/pages/organization-management')
);
const SuperAdminOrganizationPlans = lazy(
  () => import('@features/super-admin/pages/organization-plans')
);
const SuperAdminKanban = lazy(() => import('@features/super-admin/pages/super-admin-kanban'));
const SuperAdminLogistics = lazy(() => import('@features/super-admin/pages/super-admin-logistics'));
const SuperAdminBillingManagement = lazy(
  () => import('@features/super-admin/pages/billing-management')
);
const SuperAdminPaymentManagement = lazy(
  () => import('@features/super-admin/pages/payment-management')
);
const SuperAdminAuditLogs = lazy(() => import('@features/super-admin/pages/audit-logs'));
const SuperAdminNotifications = lazy(() => import('@features/super-admin/pages/notifications'));
const SuperAdminReportsAnalytics = lazy(
  () => import('@features/super-admin/pages/reports-analytics')
);
const SuperAdminDashboardSettings = lazy(
  () => import('@features/super-admin/pages/dashboard-settings')
);
const SuperAdminVerifications = lazy(() => import('@features/super-admin/pages/verifications'));

// Role-based component routers
const QuotesRouter = () => {
  const { user } = useAuth();
  if (user?.role === 'doctor' || user?.role === 'admin') return <DoctorQuotesPage />;
  return <PatientQuotesPage />;
};

const PaymentsRouter = () => {
  const { user } = useAuth();
  if (user?.role === 'admin') return <AdminPaymentsPage />;
  if (user?.role === 'doctor') return <DoctorPaymentsPage />;
  return <PatientPaymentsPage />;
};

const CalendarRouter = () => {
  const { user } = useAuth();
  if (user?.role === 'admin') return <AdminCalendarPage />;
  return <DoctorCalendarPage />;
};

const PatientsRouter = () => {
  const { user } = useAuth();
  if (user?.role === 'admin' || user?.role === 'super_admin') return <AdminPatientsPage />;
  return <DoctorPatientsPage />;
};

const NavigateToDoctorProfile = () => {
  const { slug } = useParams();
  return <Navigate to={`/doctors/${slug}`} replace />;
};

const RedirectToDefaultLocale = () => {
  const location = useLocation();
  const { locale } = useLocale();
  const currentUrl = `${location.pathname}${location.search}${location.hash}`;
  const destination = localizePath(
    currentUrl,
    getLocaleFromPathname(location.pathname) ?? locale ?? resolvePreferredLocale(location.pathname)
  );
  if (destination === currentUrl) return <RouteErrorBoundary />;
  return <Navigate to={destination} replace />;
};

const chatRedirectTarget = MESSAGING_ENABLED ? '/messages' : '/dashboard';

// Children of the persistent AppShell layout route.
// AppShell mounts once; Outlet swaps only the page content on navigation.
//
// No per-route <Suspense> here — suspensions from lazy components propagate
// up to AppShell's own persistent <Suspense> boundary. Because that boundary
// already has committed content, React 18's startTransition behaviour keeps
// the OLD page visible until the new chunk is ready, producing zero flicker.
const shellChildren = [
  { path: '/dashboard', element: <DashboardRouter /> },
  { path: '/settings', element: <UserSettingsPage /> },
  { path: '/quotes', element: <QuotesRouter /> },
  { path: '/payments', element: <PaymentsRouter /> },
  {
    path: '/patients',
    element: (
      <RouteGuard requiredRoles={['doctor', 'admin', 'super_admin']}>
        <PatientsRouter />
      </RouteGuard>
    ),
  },
  {
    path: '/calendar',
    element: (
      <RouteGuard requiredRoles={['doctor', 'admin']}>
        <CalendarRouter />
      </RouteGuard>
    ),
  },
  {
    path: '/travel',
    element: (
      <RouteGuard requiredRoles={['patient']}>
        <PatientTravelPage />
      </RouteGuard>
    ),
  },
  {
    path: '/treatment',
    element: (
      <RouteGuard requiredRoles={['patient']}>
        <PatientTreatmentPage />
      </RouteGuard>
    ),
  },
  {
    path: '/my-day',
    element: (
      <RouteGuard requiredRoles={['doctor', 'admin']}>
        <DoctorMyDayPage />
      </RouteGuard>
    ),
  },
  {
    path: '/templates',
    element: (
      <RouteGuard requiredRoles={['doctor', 'admin']}>
        <DoctorTemplatesPage />
      </RouteGuard>
    ),
  },
  ...(MESSAGING_ENABLED
    ? [{ path: '/messages/:roomSlug?', element: <MessagesPage /> }]
    : [{ path: '/messages/:roomSlug?', element: <Navigate to='/dashboard' replace /> }]),
  {
    path: '/organization/manage',
    element: (
      <RouteGuard requiredRoles={['doctor']}>
        <DoctorOrganizationManagePage />
      </RouteGuard>
    ),
  },
  {
    path: '/doctor-profile',
    element: (
      <RouteGuard requiredRoles={['doctor']}>
        <DoctorProfilePage />
      </RouteGuard>
    ),
  },
  {
    path: '/quoting-queue',
    element: (
      <RouteGuard requiredRoles={['admin']}>
        <AdminQuotingQueuePage />
      </RouteGuard>
    ),
  },
  {
    path: '/logistics',
    element: (
      <RouteGuard requiredRoles={['admin']}>
        <AdminLogisticsPage />
      </RouteGuard>
    ),
  },
  {
    path: '/sales',
    element: (
      <RouteGuard requiredRoles={['admin']}>
        <AdminSalesPage />
      </RouteGuard>
    ),
  },
  {
    path: '/contracts',
    element: (
      <RouteGuard requiredRoles={['admin']}>
        <AdminContractsPage />
      </RouteGuard>
    ),
  },
  {
    path: '/reports',
    element: (
      <RouteGuard requiredRoles={['admin']}>
        <AdminReportsPage />
      </RouteGuard>
    ),
  },
  {
    path: '/admin/users',
    element: (
      <RouteGuard requiredRoles={['admin', 'super_admin']}>
        <AdminUserManagementPage />
      </RouteGuard>
    ),
  },
  {
    path: '/admin/doctor-control/*',
    element: (
      <RouteGuard requiredRoles={['admin', 'super_admin']}>
        <AdminDoctorAccountControlWorkspacePage />
      </RouteGuard>
    ),
  },
  {
    path: '/admin/travel-planning/:travelCaseId',
    element: (
      <RouteGuard requiredRoles={['admin', 'super_admin']}>
        <AdminTravelPlanningPage />
      </RouteGuard>
    ),
  },
  {
    path: '/admin/procedure-catalog',
    element: (
      <RouteGuard requiredRoles={['admin', 'super_admin']}>
        <AdminSpecialtyProcedureCatalogPage />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/users',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminUserManagement />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/organizations',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminOrganizationManagement />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/verifications',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminVerifications />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/plans',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminOrganizationPlans />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/procedure-catalog',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <AdminSpecialtyProcedureCatalogPage />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/kanban',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminKanban />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/logistics',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminLogistics />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/billing',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminBillingManagement />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/payments',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminPaymentManagement />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/audit',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminAuditLogs />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/notifications',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminNotifications />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/reports',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminReportsAnalytics />
      </RouteGuard>
    ),
  },
  {
    path: '/super-admin/settings',
    element: (
      <RouteGuard requiredRoles={['super_admin']}>
        <SuperAdminDashboardSettings />
      </RouteGuard>
    ),
  },
];

const baseRoutes = [
  {
    path: '/',
    element: <App />,
    errorElement: <RouteErrorBoundary />,
  },

  // Public portal routes (token-gated, no auth required)
  {
    path: '/docs/:token',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <DocsPortalOtpPage />
      </Suspense>
    ),
  },
  {
    path: '/docs/:token/upload',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <DocsPortalUploadPage />
      </Suspense>
    ),
  },
  {
    path: '/flights/:token',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <FlightsPortalOtpPage />
      </Suspense>
    ),
  },
  {
    path: '/flights/:token/form',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <FlightsPortalFormPage />
      </Suspense>
    ),
  },

  // Authentication routes (no AppShell)
  {
    path: '/auth/signin',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <SignIn />
      </Suspense>
    ),
  },
  {
    path: '/auth/patient/signin',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <PatientSignIn />
      </Suspense>
    ),
  },
  {
    path: '/partner/login',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <StaffSignIn />
      </Suspense>
    ),
  },
  {
    path: '/partner/forgot-password',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <ForgotPassword />
      </Suspense>
    ),
  },
  {
    path: '/auth/reset-password',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <ResetPassword />
      </Suspense>
    ),
  },
  {
    path: '/auth/callback',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <MagicLinkCallback />
      </Suspense>
    ),
  },
  {
    path: '/auth/magic-link-required',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <MagicLinkRequired />
      </Suspense>
    ),
  },
  {
    path: '/auth/error',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <AuthError />
      </Suspense>
    ),
  },
  {
    path: '/auth/setup-password',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <SetupPassword />
      </Suspense>
    ),
  },

  // Authenticated routes that use their own layout (no AppShell)
  {
    path: '/doctor/invitations/accept',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <DoctorInvitationAcceptPage />
      </Suspense>
    ),
  },
  {
    path: '/ownership-transfer/:token',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <OwnershipTransferAcceptPage />
      </Suspense>
    ),
  },
  {
    path: '/doctor-account-control/approve/:token',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <DoctorAccountControlApprovalPage />
      </Suspense>
    ),
  },
  {
    path: '/doctor/onboarding/*',
    element: (
      <RouteGuard requiredRoles={['doctor']}>
        <Suspense fallback={<LoadingSkeleton variant='dashboard' />}>
          <DoctorOnboarding />
        </Suspense>
      </RouteGuard>
    ),
  },
  {
    path: '/doctor/quick-onboarding',
    element: (
      <RouteGuard>
        <Suspense fallback={<LoadingSkeleton variant='dashboard' />}>
          <DoctorQuickOnboardingPage />
        </Suspense>
      </RouteGuard>
    ),
  },
  {
    path: '/doctor/didit-callback',
    element: (
      <RouteGuard>
        <Suspense fallback={<LoadingSkeleton />}>
          <DoctorDiditCallbackPage />
        </Suspense>
      </RouteGuard>
    ),
  },
  {
    path: '/doctors/public/:slug',
    element: <NavigateToDoctorProfile />,
  },
  {
    path: '/doctors/:slug',
    element: (
      <Suspense fallback={<LoadingSkeleton />}>
        <DoctorPublicProfilePage />
      </Suspense>
    ),
  },

  // Legacy route redirects (no AppShell needed)
  { path: '/chat', element: <Navigate to={chatRedirectTarget} replace /> },
  { path: '/admin/usermanagement', element: <Navigate to='/admin/users' replace /> },
  { path: '/admin/user-management', element: <Navigate to='/admin/users' replace /> },
  { path: '/super-admin/patients', element: <Navigate to='/patients' replace /> },
  { path: '/super-admin/usermanagement', element: <Navigate to='/super-admin/users' replace /> },
  { path: '/super-admin/user-management', element: <Navigate to='/super-admin/users' replace /> },
  { path: '/super-admin/messages', element: <Navigate to={chatRedirectTarget} replace /> },
  { path: '/home', element: <Navigate to='/dashboard' replace /> },
  { path: '/overview', element: <Navigate to='/dashboard' replace /> },

  // Persistent layout route: AppShell renders once and Outlet swaps page content.
  // All authenticated dashboard routes are children so the sidebar/header never
  // remounts during in-app navigation.
  {
    element: (
      <RouteGuard>
        <AppShell />
      </RouteGuard>
    ),
    children: shellChildren,
  },
];

export const router = createBrowserRouter([
  ...localizeRoutes(baseRoutes),
  {
    path: '*',
    element: <RedirectToDefaultLocale />,
  },
]);
